import { Component, OnInit } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '../../../environments/environment';
import { first } from 'rxjs';
import {
    AuthorizeRequestParams,
    AuthorizeValidateResult,
    OAuthAuthorizeService,
} from './oauth-authorize.service';

type ViewState = 'loading' | 'consent' | 'not_admin' | 'redirecting' | 'error';

/** i18n label keys per scope wire value (§1 of the reference). */
const SCOPE_LABEL_KEYS: Record<string, string> = {
    'read:organization': 'aumico_outbound_api_scope_read_organization',
    'read:companies': 'aumico_outbound_api_scope_read_companies',
    'read:financials': 'aumico_outbound_api_scope_read_financials',
    'write:mappings': 'aumico_outbound_api_scope_write_mappings',
};

/**
 * The browser-facing OAuth authorization endpoint (reference §2 steps 3–4,
 * REV-21656). Deliberately guard-free: a signed-out user is sent through
 * login with a return URL; every invalid request renders an error HERE and
 * never redirects, so a minted code can only ever travel to a registered
 * redirect_uri. The backend re-validates everything on mint.
 */
@UntilDestroy()
@Component({
    selector: 'app-oauth-authorize',
    templateUrl: './oauth-authorize.page.html',
    styleUrls: ['./oauth-authorize.page.scss'],
})
export class OAuthAuthorizePage implements OnInit {
    public state: ViewState = 'loading';
    public errorKey = 'aumico_oauth_authorize_error_invalid_request';
    public clientName = '';
    /** The scopes shown on the consent screen — the delta on a scope extension. */
    public consentScopeKeys: string[] = [];
    public isScopeExtension = false;
    public busy = false;

    private params: AuthorizeRequestParams | null = null;
    private redirectUri: string | null = null;
    private returnedState: string | null = null;
    private isAdmin = false;

    public constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly auth: Auth,
        private readonly authorizeService: OAuthAuthorizeService,
    ) {}

    public ngOnInit(): void {
        const query = this.route.snapshot.queryParamMap;
        const responseType = query.get('response_type');
        const clientId = query.get('client_id');
        const redirectUri = query.get('redirect_uri');
        const scope = query.get('scope');
        const state = query.get('state');
        const codeChallenge = query.get('code_challenge');
        const codeChallengeMethod = query.get('code_challenge_method');

        if (
            responseType !== 'code' ||
            codeChallengeMethod !== 'S256' ||
            !clientId ||
            !redirectUri ||
            !scope ||
            !state ||
            !codeChallenge
        ) {
            this.fail('aumico_oauth_authorize_error_invalid_request');
            return;
        }

        this.params = { clientId, redirectUri, scope, state, codeChallenge, codeChallengeMethod };
        this.redirectUri = redirectUri;
        this.returnedState = state;

        authState(this.auth)
            .pipe(first(), untilDestroyed(this))
            .subscribe((user) => {
                if (user == null) {
                    this.redirectToLogin();
                    return;
                }
                void user.getIdTokenResult().then((token) => {
                    const roles = (token.claims['roles'] as string[] | undefined) ?? [];
                    this.isAdmin = roles.includes('ORG_ADMIN');
                    this.validate();
                });
            });
    }

    public onApprove(): void {
        if (this.params == null || this.busy) {
            return;
        }
        this.busy = true;
        this.authorizeService.approveAndMint(this.params).subscribe({
            next: ({ redirectTo }) => {
                this.state = 'redirecting';
                window.location.assign(redirectTo);
            },
            error: () => {
                this.busy = false;
                this.fail('aumico_oauth_authorize_error_generic');
            },
        });
    }

    /** RFC 6749 §4.1.2.1 — denial goes back to the (validated) redirect_uri. */
    public onDeny(): void {
        if (this.redirectUri == null || this.returnedState == null) {
            return;
        }
        const target = new URL(this.redirectUri);
        target.searchParams.set('error', 'access_denied');
        target.searchParams.set('state', this.returnedState);
        this.state = 'redirecting';
        window.location.assign(target.toString());
    }

    private validate(): void {
        if (this.params == null) {
            return;
        }
        this.authorizeService
            .validate(this.params)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (result) => this.onValidated(result),
                error: () => this.fail('aumico_oauth_authorize_error_generic'),
            });
    }

    private onValidated(result: AuthorizeValidateResult): void {
        if (!result.valid) {
            const reasonKeys: Record<string, string> = {
                unknown_client: 'aumico_oauth_authorize_error_unknown_client',
                redirect_uri_not_registered: 'aumico_oauth_authorize_error_redirect_uri',
                invalid_scope: 'aumico_oauth_authorize_error_invalid_scope',
            };
            this.fail(reasonKeys[result.reason ?? ''] ?? 'aumico_oauth_authorize_error_invalid_request');
            return;
        }
        this.clientName = result.clientName ?? '';
        // Only ORG_ADMIN may mint — everyone else gets the "ask your admin" view.
        if (!this.isAdmin) {
            this.state = 'not_admin';
            return;
        }
        if (result.consented === true) {
            // Consent already covers the request — approve silently (§2 step 4).
            this.onApprove();
            return;
        }
        const scopes = result.scopes ?? [];
        const newScopes = result.newScopes ?? [];
        this.isScopeExtension = newScopes.length > 0 && newScopes.length < scopes.length;
        const shown = this.isScopeExtension ? newScopes : scopes;
        this.consentScopeKeys = shown.map((scope) => SCOPE_LABEL_KEYS[scope] ?? scope);
        this.state = 'consent';
    }

    private redirectToLogin(): void {
        const language = localStorage.getItem('currentLanguage') ?? environment.defaultLanguage;
        const returnUrl = this.router.url;
        void this.router.navigate([`/${language}/auth/login`], { queryParams: { returnUrl } });
    }

    private fail(errorKey: string): void {
        this.errorKey = errorKey;
        this.state = 'error';
    }
}
